import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Loader2, 
  Users, 
  Clock, 
  Calendar,
  MapPin,
  Globe,
  Sun,
  Palmtree,
  Briefcase,
  ChevronDown,
  ChevronRight,
  PartyPopper,
  Check,
  X,
  FileSpreadsheet,
  FileText,
  Euro,
  Bus,
  Route,
  Timer,
  Calculator,
  ChevronUp,
  Pencil,
  MessageSquare
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Rate selection options
const RATE_OPTIONS = [
  { value: 'none', label: '0' },
  { value: 'half', label: 'Пол ставки' },
  { value: 'full', label: 'Ставка' },
  { value: '+1', label: '+1' },
  { value: '+2', label: '+2' },
  { value: '+3', label: '+3' },
  { value: '+4', label: '+4' },
  { value: '+5', label: '+5' },
  { value: '+6', label: '+6' },
  { value: '+7', label: '+7' },
  { value: '+8', label: '+8' },
  { value: '+9', label: '+9' },
  { value: '+10', label: '+10' },
];

// Bus category icon colors
const getCategoryColor = (category) => {
  switch (category) {
    case 'small': return 'text-green-500';
    case 'medium': return 'text-blue-500';
    case 'large': return 'text-purple-500';
    default: return 'text-gray-400';
  }
};

export default function SalaryReport() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [expandedDriver, setExpandedDriver] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [editingTripId, setEditingTripId] = useState(null);
  const [savingRate, setSavingRate] = useState(false);
  const [transferring, setTransferring] = useState(null);
  const [salarySettings, setSalarySettings] = useState({ overtimeBonus: 5, baseHours: 10 });
  
  // Manual salary editing
  const [editingSalaryTripId, setEditingSalaryTripId] = useState(null);
  const [manualSalaryValue, setManualSalaryValue] = useState('');

  // Fetch salary settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/api/salary-settings`, getAuthHeaders());
        const formula = response.data.formula_settings || {};
        setSalarySettings({
          overtimeBonus: formula.overtime_bonus || 5,
          baseHours: formula.base_hours || 10
        });
      } catch (error) {
        console.error('Error fetching salary settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Get month dates
  const getMonthDates = (offset = 0) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + offset;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const formatDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    
    return {
      start: formatDate(firstDay),
      end: formatDate(lastDay),
      label: firstDay.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
    };
  };

  const fetchReport = async (startDate, endDate) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/salary-report?start_date=${startDate}&end_date=${endDate}`,
        getAuthHeaders()
      );
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching salary report:', error);
      toast.error('Ошибка загрузки отчёта');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const dates = getMonthDates(0);
    setCustomStartDate(dates.start);
    setCustomEndDate(dates.end);
    fetchReport(dates.start, dates.end);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    let dates;
    if (tab === 'current') {
      dates = getMonthDates(0);
      setCustomStartDate(dates.start);
      setCustomEndDate(dates.end);
      fetchReport(dates.start, dates.end);
    } else if (tab === 'previous') {
      dates = getMonthDates(-1);
      setCustomStartDate(dates.start);
      setCustomEndDate(dates.end);
      fetchReport(dates.start, dates.end);
    }
  };

  const handleCustomPeriod = () => {
    if (customStartDate && customEndDate) {
      fetchReport(customStartDate, customEndDate);
    }
  };

  // Get selection label from rate_selection value
  const getSelectionLabel = (rateSelection) => {
    if (!rateSelection || rateSelection === 'none') return '0';
    if (rateSelection === 'half') return 'Пол ставки';
    if (rateSelection === 'full') return 'Ставка';
    if (rateSelection.startsWith('+')) return `Ставка ${rateSelection}`;
    return '0';
  };

  // Round up to nearest 5 (in driver's favor)
  const roundUpTo5 = (value) => Math.ceil(value / 5) * 5;

  // Calculate salary based on rate selection (using settings from server)
  const calculateSalary = (rate, rateSelection) => {
    const { baseHours, overtimeBonus } = salarySettings;
    
    if (!rateSelection || rateSelection === 'none') return 0;
    if (rateSelection === 'half') {
      const rawTotal = (baseHours * 0.5) * rate;
      return roundUpTo5(rawTotal); // 36 -> 40, 41 -> 45, 28 -> 30
    }
    if (rateSelection === 'full') return baseHours * rate;
    if (rateSelection.startsWith('+')) {
      // Ставка + N × overtime_bonus
      const extra = parseInt(rateSelection.slice(1)) || 0;
      const basePay = baseHours * rate;
      const extraPay = extra * overtimeBonus;
      return basePay + extraPay;
    }
    return 0;
  };

  // Quick set to "full" (Ставка)
  const handleQuickFullRate = async (tripId, rate) => {
    await handleRateSelection(tripId, 'full');
  };

  // Update rate selection for a trip
  const handleRateSelection = async (tripId, rateSelection) => {
    setSavingRate(true);
    
    // Optimistic update - only update selection_label to show immediate feedback
    setReport(prevReport => {
      if (!prevReport) return prevReport;
      
      const newDrivers = prevReport.drivers.map(driver => {
        const newTrips = driver.trips.map(trip => {
          if (trip.trip_id === tripId) {
            return {
              ...trip,
              rate_selection: rateSelection,
              selection_label: getSelectionLabel(rateSelection)
              // Don't update salary - wait for server
            };
          }
          return trip;
        });
        
        return {
          ...driver,
          trips: newTrips
        };
      });
      
      return {
        ...prevReport,
        drivers: newDrivers
      };
    });
    
    setEditingTripId(null);
    
    try {
      await axios.put(
        `${API}/api/trips/${tripId}/rate-selection`,
        { rate_selection: rateSelection },
        getAuthHeaders()
      );
      
      // Fetch updated data from server to get correct salary
      const currentExpanded = expandedDriver;
      const response = await axios.get(
        `${API}/api/salary-report?start_date=${customStartDate}&end_date=${customEndDate}`,
        getAuthHeaders()
      );
      setReport(response.data);
      setExpandedDriver(currentExpanded); // Keep driver expanded
      
      toast.success('Сохранено');
    } catch (error) {
      console.error('Error updating rate selection:', error);
      toast.error('Ошибка сохранения');
      fetchReport(customStartDate, customEndDate);
    } finally {
      setSavingRate(false);
    }
  };

  // Save manual salary for a trip
  const handleSaveManualSalary = async (tripId) => {
    const salary = parseFloat(manualSalaryValue) || 0;
    setSavingRate(true);
    
    try {
      await axios.put(
        `${API}/api/trips/${tripId}`,
        { driver_salary: salary },
        getAuthHeaders()
      );
      
      // Update local state
      setReport(prevReport => {
        if (!prevReport) return prevReport;
        const newDrivers = prevReport.drivers.map(driver => {
          const newTrips = driver.trips.map(trip => {
            if (trip.trip_id === tripId) {
              return { ...trip, salary: salary };
            }
            return trip;
          });
          // Recalculate total
          const newTotalSalary = newTrips.reduce((sum, t) => sum + (t.salary || 0), 0);
          return { ...driver, trips: newTrips, total_salary: newTotalSalary, base_salary: newTotalSalary };
        });
        return { ...prevReport, drivers: newDrivers };
      });
      
      setEditingSalaryTripId(null);
      setManualSalaryValue('');
      toast.success('Зарплата сохранена');
    } catch (error) {
      console.error('Error saving manual salary:', error);
      toast.error('Ошибка сохранения');
    } finally {
      setSavingRate(false);
    }
  };

  // Transfer salaries to Excel
  const handleTransferToExcel = async (driverId, upToDate) => {
    setTransferring(driverId);
    try {
      const response = await axios.post(
        `${API}/api/salary-report/transfer-to-excel`,
        { 
          driver_id: driverId, 
          up_to_date: upToDate,
          start_date: customStartDate 
        },
        getAuthHeaders()
      );
      toast.success(`Перенесено ${response.data.updated_trips} рейсов. Итого: ${response.data.total_salary}€`);
    } catch (error) {
      console.error('Error transferring salaries:', error);
      toast.error('Ошибка переноса зарплат');
    } finally {
      setTransferring(null);
    }
  };

  const formatRubles = (num) => {
    if (num == null) return '0';
    return num.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const exportExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/api/salary-report/export/excel?start_date=${customStartDate}&end_date=${customEndDate}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `salary_report_${customStartDate}_${customEndDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel файл скачан');
    } catch (error) {
      toast.error('Ошибка экспорта');
    }
  };

  const exportPdf = async () => {
    try {
      const response = await axios.get(
        `${API}/api/salary-report/export/pdf?start_date=${customStartDate}&end_date=${customEndDate}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `salary_report_${customStartDate}_${customEndDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF файл скачан');
    } catch (error) {
      toast.error('Ошибка экспорта');
    }
  };

  return (
    <div className="space-y-4">
      {/* Period Selection */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Tabs */}
          <div className="flex rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => handleTabChange('current')}
              className={`px-4 py-2 text-sm rounded-md transition-colors ${
                activeTab === 'current' ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {getMonthDates(0).label}
            </button>
            <button
              onClick={() => handleTabChange('previous')}
              className={`px-4 py-2 text-sm rounded-md transition-colors ${
                activeTab === 'previous' ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {getMonthDates(-1).label}
            </button>
            <button
              onClick={() => setActiveTab('custom')}
              className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-1 ${
                activeTab === 'custom' ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Период
            </button>
          </div>

          {/* Custom dates */}
          {activeTab === 'custom' && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-36"
              />
              <span className="text-gray-400">—</span>
              <Input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-36"
              />
              <Button onClick={handleCustomPeriod} size="sm">
                Показать
              </Button>
            </div>
          )}

          {/* Export buttons */}
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" size="sm" onClick={exportExcel}>
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={exportPdf}>
              <FileText className="w-4 h-4 mr-1" />
              PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Водителей</p>
                <p className="text-3xl font-bold">{report.totals.drivers_count}</p>
              </div>
              <Users className="w-10 h-10 text-blue-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Всего зарплата</p>
                <p className="text-3xl font-bold">{formatRubles(report.totals.total_base_salary || report.totals.total_salary)}€</p>
              </div>
              <Euro className="w-10 h-10 text-emerald-200" />
            </div>
          </div>
        </div>
      )}

      {/* Drivers List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : report?.drivers?.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {report.drivers.map((driver, idx) => (
            <div key={driver.driver_id} className={idx > 0 ? 'border-t' : ''}>
              {/* Driver Row */}
              <div
                className="flex items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedDriver(expandedDriver === driver.driver_id ? null : driver.driver_id)}
              >
                {/* Expand indicator */}
                <div className="w-6 mr-2">
                  {expandedDriver === driver.driver_id ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>

                {/* Driver info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{driver.driver_name}</p>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Route className="w-3 h-3" />
                      {driver.trips?.length || 0} рейсов
                    </span>
                    <span className="flex items-center gap-1">
                      <Timer className="w-3 h-3" />
                      {driver.total_hours}ч
                    </span>
                  </div>
                </div>

                {/* Breakdown badges */}
                <div className="hidden md:flex items-center gap-2 mr-4">
                  {driver.by_region?.domestic?.salary > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-50 text-blue-700">
                      <MapPin className="w-3 h-3" />
                      {formatRubles(driver.by_region.domestic.salary)}€
                    </span>
                  )}
                  {driver.by_region?.international?.salary > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-purple-50 text-purple-700">
                      <Globe className="w-3 h-3" />
                      {formatRubles(driver.by_region.international.salary)}€
                    </span>
                  )}
                </div>

                {/* Total */}
                <div className="text-right">
                  <p className="text-xl font-bold text-emerald-600">{formatRubles(driver.base_salary || driver.total_salary)}€</p>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedDriver === driver.driver_id && (
                <div className="bg-gray-50 border-t">
                  {/* Trips Table */}
                  {driver.trips?.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 text-gray-600">
                          <tr>
                            <th className="px-3 py-3 text-left font-medium">Дата</th>
                            <th className="px-3 py-3 text-left font-medium">Маршрут</th>
                            <th className="px-3 py-3 text-left font-medium">Описание</th>
                            <th className="px-3 py-3 text-center font-medium">Время</th>
                            <th className="px-3 py-3 text-center font-medium">Тип</th>
                            <th className="px-3 py-3 text-center font-medium">Автобус</th>
                            <th className="px-3 py-3 text-center font-medium">Ставка</th>
                            <th className="px-3 py-3 text-right font-medium">Сумма</th>
                            <th className="px-3 py-3 text-center font-medium">Расчёт</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // Group trips by date for zebra striping
                            const uniqueDates = [...new Set(driver.trips.map(t => t.date))];
                            return driver.trips.map((trip, i) => {
                              const dateIndex = uniqueDates.indexOf(trip.date);
                              const isEvenDate = dateIndex % 2 === 0;
                              return (
                            <tr key={i} className={`border-t hover:bg-blue-100 transition-colors ${isEvenDate ? 'bg-white' : 'bg-slate-100'}`}>
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-2">
                                  <span className="whitespace-nowrap">{trip.date}</span>
                                  {trip.is_holiday ? (
                                    <PartyPopper className="w-4 h-4 text-amber-500" title="Праздник" />
                                  ) : trip.rate_type === 'weekend_season_rate' ? (
                                    <Palmtree className="w-4 h-4 text-green-500" title="Сезонный выходной" />
                                  ) : trip.is_weekend ? (
                                    <Sun className="w-4 h-4 text-orange-400" title="Выходной" />
                                  ) : null}
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-2">
                                  {trip.region === 'international' ? (
                                    <Globe className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                  ) : (
                                    <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  )}
                                  <span className="truncate max-w-[150px]">{trip.route_name || '—'}</span>
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <span className="text-gray-500 text-xs truncate max-w-[120px] block" title={trip.description || ''}>
                                  {trip.description || '—'}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-center text-gray-500 whitespace-nowrap">
                                {trip.time_start}-{trip.time_end}
                              </td>
                              <td className="px-3 py-3 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {/* Quick "Ставка" button */}
                                  {(!trip.rate_selection || trip.rate_selection === 'none') && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleQuickFullRate(trip.trip_id, trip.rate);
                                      }}
                                      className="p-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded transition-colors"
                                      title="Быстро выбрать Ставка"
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                  )}
                                  {editingTripId === trip.trip_id ? (
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                      <Select 
                                        defaultValue={trip.rate_selection || 'none'}
                                        onValueChange={(val) => handleRateSelection(trip.trip_id, val)}
                                      >
                                        <SelectTrigger className="w-28 h-8">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {RATE_OPTIONS.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>
                                              {opt.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      {savingRate && <Loader2 className="w-4 h-4 animate-spin" />}
                                      <button
                                        onClick={() => setEditingTripId(null)}
                                        className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingTripId(trip.trip_id);
                                      }}
                                      className={`font-medium px-3 py-1 rounded transition-colors border ${
                                        trip.rate_selection && trip.rate_selection !== 'none' 
                                          ? 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100' 
                                          : 'text-gray-900 hover:bg-blue-50 hover:text-blue-700'
                                      }`}
                                      title="Изменить тип"
                                    >
                                      {trip.rate_selection ? (trip.selection_label || trip.rate_selection) : '0'}
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-3 text-center">
                                <span className="inline-flex items-center gap-1 text-xs" title={trip.category === 'small' ? '≤23 мест' : trip.category === 'medium' ? '24-34 мест' : '35+ мест'}>
                                  <Bus className={`w-4 h-4 ${getCategoryColor(trip.category)}`} />
                                  <span className="font-medium">{trip.bus_license || '—'}</span>
                                </span>
                              </td>
                              <td className="px-3 py-3 text-center text-gray-600">
                                {formatRubles(trip.rate * 10)}€
                              </td>
                              <td className="px-3 py-3 text-right">
                                {editingSalaryTripId === trip.trip_id ? (
                                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                    <Input
                                      type="number"
                                      value={manualSalaryValue}
                                      onChange={(e) => setManualSalaryValue(e.target.value)}
                                      className="w-20 h-7 text-right text-sm"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveManualSalary(trip.trip_id);
                                        if (e.key === 'Escape') { setEditingSalaryTripId(null); setManualSalaryValue(''); }
                                      }}
                                    />
                                    <button
                                      onClick={() => handleSaveManualSalary(trip.trip_id)}
                                      className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => { setEditingSalaryTripId(null); setManualSalaryValue(''); }}
                                      className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingSalaryTripId(trip.trip_id);
                                      setManualSalaryValue(trip.salary?.toString() || '0');
                                    }}
                                    className={`font-semibold px-2 py-1 rounded transition-colors ${
                                      trip.is_manual_salary 
                                        ? 'text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-300' 
                                        : 'text-emerald-600 hover:bg-emerald-50'
                                    }`}
                                    title={trip.is_manual_salary ? "Ручной ввод (нажмите для изменения)" : "Нажмите для ручного ввода зарплаты"}
                                  >
                                    {trip.is_manual_salary && <Pencil className="w-3 h-3 inline mr-1" />}
                                    {formatRubles(trip.salary)}€
                                  </button>
                                )}
                              </td>
                              <td className="px-3 py-3 text-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTransferToExcel(driver.driver_id, trip.date);
                                  }}
                                  disabled={transferring === driver.driver_id}
                                  className="h-7 px-2"
                                  title={`Перенести зарплаты до ${trip.date} в Excel`}
                                >
                                  {transferring === driver.driver_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Calculator className="w-4 h-4" />
                                  )}
                                </Button>
                              </td>
                            </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : report ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-200" />
          <p className="text-gray-500">Нет данных за выбранный период</p>
        </div>
      ) : null}

      {/* Legend */}
      {report && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 mb-3">Обозначения</p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-600">
            <span className="flex items-center gap-1"><Briefcase className="w-3 h-3 text-gray-400" /> Рабочий</span>
            <span className="flex items-center gap-1"><Sun className="w-3 h-3 text-orange-400" /> Выходной</span>
            <span className="flex items-center gap-1"><Palmtree className="w-3 h-3 text-green-500" /> Сезон (май-сент)</span>
            <span className="flex items-center gap-1"><PartyPopper className="w-3 h-3 text-amber-500" /> Праздник</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-blue-500" /> Местный</span>
            <span className="flex items-center gap-1"><Globe className="w-3 h-3 text-purple-500" /> Международный</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t text-xs text-gray-500">
            <span className="font-medium text-gray-700">Категории автобусов:</span>
            <span className="flex items-center gap-1"><Bus className="w-4 h-4 text-green-500" /> До 23 мест</span>
            <span className="flex items-center gap-1"><Bus className="w-4 h-4 text-blue-500" /> 24-34 мест</span>
            <span className="flex items-center gap-1"><Bus className="w-4 h-4 text-purple-500" /> 35-60 мест</span>
          </div>
          <div className="mt-3 pt-3 border-t text-xs text-gray-500">
            <p><strong>Ставка:</strong> до 4ч = пол ставки, 4-10ч = ставка, более 10ч = ставка + доплата</p>
            <p><strong>Расчёт:</strong> Нажмите на кнопку калькулятора, чтобы перенести зарплаты до этой даты в Excel</p>
            <p><strong>Сумма:</strong> Нажмите на сумму, чтобы ввести зарплату вручную</p>
          </div>
        </div>
      )}
    </div>
  );
}
